import {
  productsKindOf,
  profileLayoutOf,
  profileTitle,
  sectionsHolding,
  shopDataUrl,
  shopLogoUrl,
  type PublicShop,
} from "@fusiqo/vozqi-shared";
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { ProfileAbout } from "./profile-about";
import { ProfileActions } from "./profile-actions";
import { ProfileHero } from "./profile-hero";
import { ProfileHours } from "./profile-hours";
import { ProfileLocation } from "./profile-location";
import { ProfileReviews } from "./profile-reviews";
import { ProfileSignals } from "./profile-signals";
import { ProfileSocial } from "./profile-social";
import { loadSnapshot, saveSnapshot } from "./profile-snapshot";
import { PublicHeader, PublicHeaderSkeleton } from "./public-header";
import { ServicesList } from "./services-list";
import { Notice } from "./SharePage";
import { ShopProducts } from "./shop-products";
import { shopWhatsappHref, slugFromPath, type ProductsSnapshot } from "./shop-view";
import { useScrolledPast } from "./use-scrolled-past";
import { useServices } from "./use-services";
import { VozqiFoot } from "./vozqi-foot";
import { WhatsAppMark } from "./whatsapp-mark";
// oxlint-disable-next-line import/no-unassigned-import -- the profile's own stylesheet, imported for its side effect
import "./profile.css";

/** The error pages' «Hecho con VozQI» link, tagged as the profile's. */
const FOOT_HREF = "https://vozqi.com/?utm_source=perfil";

/**
 * THE BUSINESS'S PROFILE (spec 2026-10-20 §5), grown from its shop (spec
 * 2026-09-26 §6), a professional page. Top
 * to bottom: its sign -- who it is, whether it is open, the one thing to do
 * --; what it says about itself, the signals VozQI vouches for, its reviews and its
 * services, with the hours, «Dónde estamos» and «Síguenos» beside them on a
 * wide screen and after them on a phone; then its products, last because
 * they scroll without end; the foot. Each part is its own file; this one loads and
 * composes. Same light entry as the item's page: no router, no session, no kit.
 */
type State =
  | { kind: "loading" }
  | { kind: "gone" }
  | { kind: "failed" }
  | { kind: "ready"; shop: PublicShop };

export function ShopPage() {
  const slug = slugFromPath(window.location.pathname);
  const [state, setState] = useState<State>(slug === null ? { kind: "gone" } : { kind: "loading" });

  const load = useCallback(async () => {
    if (slug === null) return;
    setState({ kind: "loading" });
    try {
      const response = await fetch(shopDataUrl(slug));
      if (response.status === 404) return setState({ kind: "gone" });
      if (!response.ok) return setState({ kind: "failed" });
      setState({ kind: "ready", shop: (await response.json()) as PublicShop });
    } catch {
      setState({ kind: "failed" });
    }
  }, [slug]);

  useEffect(() => {
    void load();
  }, [load]);

  // The Worker's own title: Google keeps the one the page ends with.
  useEffect(() => {
    if (state.kind === "ready") document.title = profileTitle(state.shop);
  }, [state]);

  if (state.kind === "loading") return <ShopSkeleton />;
  if (state.kind === "gone" || slug === null) {
    return (
      <Notice title="Esta página no está disponible" footHref={FOOT_HREF}>
        <p>Pídele a quien te la envió que la comparta de nuevo.</p>
      </Notice>
    );
  }
  if (state.kind === "failed") {
    return (
      <Notice title="No pudimos cargar esto" footHref={FOOT_HREF}>
        <p>Revisa tu conexión e inténtalo otra vez.</p>
        <button type="button" className="share-retry" onClick={() => void load()}>
          Reintentar
        </button>
      </Notice>
    );
  }
  return <Profile shop={state.shop} slug={slug} />;
}

function Profile({ shop, slug }: { shop: PublicShop; slug: string }) {
  const whatsapp = shopWhatsappHref(shop);
  const logoSrc = shop.business.hasLogo ? shopLogoUrl(slug) : null;
  const name = useScrolledPast<HTMLHeadingElement>();
  const { about, signals, reviews, hours, street, pin, social } = shop.profile;
  const lead =
    about === "" && signals.length === 0 && reviews.count === 0 ? null : (
      <>
        <ProfileAbout about={about} />
        <ProfileSignals signals={signals} />
        <ProfileReviews slug={slug} reviews={reviews} businessName={shop.business.name} timezone={shop.profile.timezone} />
      </>
    );
  const rail =
    hours.length === 0 && street === null && pin === null && social.length === 0 ? null : (
      <>
        {/* «Pedir servicio» heads the rail where the rail follows the reader (profile.css, 900px and up); below, it stays on the sign. */}
        <div className="profile-rail-cta">
          <ProfileActions token={shop.profile.requestToken} requestLabel="Pedir servicio" />
        </div>
        <ProfileHours hours={hours} />
        <ProfileLocation shop={shop} />
        <ProfileSocial social={social} business={shop.business.name} />
      </>
    );
  return (
    <div className={whatsapp === null ? "shop" : "shop shop--with-fab"}>
      <PublicHeader name={shop.business.name} logoSrc={logoSrc} heading={false} shopHref={null} quiet={!name.past} />
      <ProfileHero shop={shop} logoSrc={logoSrc} nameRef={name.ref} />
      <ProfileParts shop={shop} slug={slug} lead={lead} rail={rail} />

      <VozqiFoot slug={slug} page="perfil" />

      {whatsapp !== null && (
        <a
          className="share-fab"
          href={whatsapp}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Escribir por WhatsApp"
          title="Escribir por WhatsApp"
        >
          <WhatsAppMark />
        </a>
      )}
    </div>
  );
}

const NO_PRODUCTS: ProductsSnapshot = { q: "", section: null, items: [], pageOf: {}, total: null, nextCursor: null };

/**
 * The body: `lead` (about and signals) and «Servicios» in the main column,
 * `rail` (hours, the map and the networks) beside them -- each null when it
 * has nothing to draw, and the column it leaves goes to the other --, then «Productos», or
 * the shop alone, as it was, when it holds no services. Opening a service or
 * a card saves where the visitor was, both parts and the scroll; «atrás»
 * gives each part back what it listed (a part saved empty starts afresh) and
 * lands the scroll once the services are drawn.
 */
function ProfileParts({ shop, slug, lead, rail }: { shop: PublicShop; slug: string; lead: ReactNode; rail: ReactNode }) {
  const layout = profileLayoutOf(shop.profile);
  const productsKind = productsKindOf(layout);
  const restored = useMemo(() => loadSnapshot(slug), [slug]);
  const restoredServices = useMemo(
    () =>
      restored === null || restored.services.length === 0
        ? null
        : { items: restored.services, cursor: restored.servicesCursor, pageOf: restored.servicesPageOf },
    [restored],
  );
  const services = useServices(slug, layout.services, restoredServices);
  const products = useRef<ProductsSnapshot | null>(null);
  const ready = !layout.services || services.settled;

  useEffect(() => {
    if (restored !== null && ready) window.requestAnimationFrame(() => window.scrollTo(0, restored.scrollY));
  }, [restored, ready]);

  const remember = (): void =>
    saveSnapshot({
      slug,
      ...(products.current ?? NO_PRODUCTS),
      services: services.items,
      servicesCursor: services.cursor,
      servicesPageOf: services.pageOf,
      scrollY: window.scrollY,
      savedAt: Date.now(),
    });

  const main = lead !== null || layout.services;
  return (
    <>
      {main || rail !== null ? (
        <div className={main && rail !== null ? "profile-body profile-body--rail" : "profile-body"}>
          {main ? (
            <div className="profile-main">
              {lead}
              {layout.services ? (
                <ServicesList
                  slug={slug}
                  services={services}
                  sections={sectionsHolding(shop.sections, "SERVICE")}
                  country={shop.business.country}
                  onOpen={remember}
                />
              ) : null}
            </div>
          ) : null}
          {rail === null ? null : <aside className="profile-rail">{rail}</aside>}
        </div>
      ) : null}
      {layout.products ? (
        <ShopProducts
          shop={shop}
          slug={slug}
          kind={productsKind}
          sections={sectionsHolding(shop.sections, productsKind)}
          shopWord={layout.shopWord}
          restored={restored === null || restored.items.length === 0 ? null : restored}
          latest={products}
          onOpen={remember}
        />
      ) : null}
    </>
  );
}

/** The page while it loads: the bar, the sign's awning and a few lines, so the profile grows into place. */
function ShopSkeleton() {
  return (
    <div className="shop" aria-busy="true" aria-label="Cargando">
      <PublicHeaderSkeleton />
      <div className="profile-hero">
        <div className="profile-awning" />
        <div className="profile-hero-inner">
          <div className="profile-id">
            <span className="profile-logo share-skeleton" />
            <div className="profile-id-text">
              <span className="share-skeleton share-skeleton--title" />
              <span className="share-skeleton share-skeleton--line" style={{ width: "55%" }} />
            </div>
          </div>
        </div>
      </div>
      <div className="profile-body">
        <div className="profile-services">
          {Array.from({ length: 3 }, (_, index) => (
            <div key={index} className="profile-service profile-service--skeleton share-skeleton" />
          ))}
        </div>
      </div>
    </div>
  );
}
